import { Fragment, useState } from 'react';
import { formatWireMoney } from '../money';
import { differentialText, isSyntheticRate, rateStatus } from '../rateDisplay';
import { payBasisLabels, type ServiceRate } from '../types';
import { rateCategoryLabel } from '../rateDisplay';
import { EndDateForm } from './EndDateForm';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

export interface RatesTableProps {
  /** The table's accessible name, for example "Company rate schedule". */
  label: string;
  rows: ServiceRate[];
  locationNames: ReadonlyMap<string, string>;
  /** When given, each row that has not ended gets an End action (the append-only way to change a rate). */
  onEnd?: ((rate: ServiceRate, date: string) => Promise<void>) | undefined;
  emptyMessage: string;
  /** Mark rows from the synthetic demonstration set with an "Example" badge. */
  markExamples?: boolean;
}

/** A rate schedule: one row per discipline, service type, location and pay basis, with its dates and a status in words. Rows are never edited; end one and add a new one. */
export function RatesTable({ label, rows, locationNames, onEnd, emptyMessage, markExamples = false }: RatesTableProps) {
  const [endingId, setEndingId] = useState<string | null>(null);
  const columns = onEnd ? 10 : 9;
  return (
    <div className="jr-table-scroll" role="region" aria-label={`${label} (scrolls sideways on a narrow screen)`} tabIndex={0}>
      <table className="jr-table" aria-label={label}>
        <thead>
          <tr>
            <th scope="col">Worker category</th>
            <th scope="col">Work type</th>
            <th scope="col">Location</th>
            <th scope="col">Pay basis</th>
            <th scope="col" className="num">Rate</th>
            <th scope="col">Differential</th>
            <th scope="col">Effective from</th>
            <th scope="col">Ends before</th>
            <th scope="col">Status</th>
            {onEnd && <th scope="col">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns} className="muted">{emptyMessage}</td></tr>}
          {rows.map((r) => {
            const status = rateStatus(r);
            const name = `${rateCategoryLabel(r, 'worker category')} ${r.serviceTypeName ?? 'any work type'} ${payBasisLabels[r.payBasis]}`;
            return (
              <Fragment key={r.id}>
                <tr>
                  <th scope="row">{r.appliesToAnyWorkerCategory ? 'Any worker category' : (r.disciplineName ?? '—')}</th>
                  <td>{r.serviceTypeName ?? 'Any work type'}</td>
                  <td>{r.workLocationId ? (locationNames.get(r.workLocationId) ?? 'A location') : 'All locations'}</td>
                  <td>{payBasisLabels[r.payBasis]}</td>
                  <td className="num">
                    {formatWireMoney(r.rate)}
                    {markExamples && isSyntheticRate(r) && <> <span className="hdc-badge hdc-badge--example">Example</span></>}
                  </td>
                  <td>{differentialText(r)}</td>
                  <td>{r.effectiveFrom}</td>
                  <td>{r.effectiveTo ?? 'No end date'}</td>
                  <td><span className={`hdc-badge${status === 'In effect' ? ' hdc-badge--ok' : status === 'Scheduled' ? ' hdc-badge--warn' : ''}`}>{status}</span></td>
                  {onEnd && (
                    <td>
                      {r.effectiveTo === null && endingId !== r.id && (
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-label={`End rate ${name}`} onClick={() => setEndingId(r.id)}>End rate</button>
                      )}
                    </td>
                  )}
                </tr>
                {onEnd && endingId === r.id && (
                  <tr>
                    <td colSpan={columns}>
                      <EndDateForm
                        label={`End rate ${name}`}
                        startDate={r.effectiveFrom}
                        fieldLabel="Ends before"
                        confirmLabel="Confirm end"
                        onCancel={() => setEndingId(null)}
                        onConfirm={async (date) => { await onEnd(r, date); setEndingId(null); }}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
